import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BACKEND_BASE_URL } from "../constants";
import { CustomLink, TextBackground } from "../components";
import {
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    CircularProgress,
    Box,
    Typography,
    Alert,
} from "@mui/material";

// --- Types for job status data ---
interface JobParams {
    TCRalpha: string;
    TCRbeta: string;
    peptide: string;
    mhc: string;
}

interface JobTimestamps {
    submitted_at: string;
    started_time: string | null;
    failed_time: string | null;
    completed_time: string | null;
}

interface JobStatusData {
    status: "PENDING" | "STARTED" | "SUCCESS" | "FAILURE";
    params: JobParams;
    timestamps: JobTimestamps;
    error?: string;
    tar_result_url?: string;
}
// --------------------------------------------------------

const formatDate = (isoDateString: string | null) => {
    if (!isoDateString) return 'N/A';
    const date = new Date(isoDateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
    }).format(date);
};

export const TaskStatus = () => {
    const { job_id } = useParams<{ job_id: string }>();
    const [jobData, setJobData] = useState<JobStatusData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const pollInterval = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTaskStatus = async () => {
            try {
                const response = await fetch(
                    `${BACKEND_BASE_URL}/stegg/job/${job_id}/task_status/`
                );
                if (!response.ok) {
                    throw new Error("Network response was not ok.");
                }
                const data: JobStatusData = await response.json();
                setJobData(data);
                setIsLoading(false);

                // ---  Check status and stop polling ---
                if (data.status === "SUCCESS" || data.status === "FAILURE") {
                    if (pollInterval.current) {
                        clearInterval(pollInterval.current);
                    }
                    if (data.status === "FAILURE") {
                        setError(data.error || "Job failed for an unknown reason.");
                    }
                }
            } catch (err) {
                const errorMsg = (err instanceof Error) ? err.message : "An unknown error occurred.";
                setError(errorMsg);
                setIsLoading(false);
                if (pollInterval.current) {
                    clearInterval(pollInterval.current);
                }
            }
        };

        // Immediately invoke once
        fetchTaskStatus();
        // Set interval to poll every 5 seconds
        pollInterval.current = setInterval(fetchTaskStatus, 5000);

        // Clear interval on component unmount
        return () => {
            if (pollInterval.current) {
                clearInterval(pollInterval.current);
            }
        };
    }, [job_id]);

    if (isLoading) {
        return (
            <section className="page">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading job status...</Typography>
                </Box>
            </section>
        );
    }

    if (error) {
        return (
            <section className="page">
                <TextBackground>
                    <Typography variant="h4" color="error" gutterBottom>Job Failed</Typography>
                    <Alert severity="error">{error}</Alert>
                </TextBackground>
            </section>
        );
    }

    if (!jobData) {
        return (
            <section className="page">
                <TextBackground>
                    <Typography>No task data found.</Typography>
                </TextBackground>
            </section>
        );
    }

    const isRunning = jobData.status === "PENDING" || jobData.status === "STARTED";
    const isSuccess = jobData.status === "SUCCESS";
    const isFailure = jobData.status === "FAILURE";
    
    // URL to results page
    const resultsPageUrl = `/results/${job_id}`;

    return (
        <section className="page">
            <TextBackground>
                <Typography variant="h4" gutterBottom>
                    {isSuccess ? "Your job has finished!" : (isFailure ? "Your job has failed" : "Your job is processing...")}
                </Typography>
                <Typography variant="h5" sx={{ mb: 2 }}>Job Status: {jobData.status}</Typography>

                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table aria-label="job status table">
                        <TableBody>
                            <TableRow>
                                <TableCell component="th" scope="row">Job ID</TableCell>
                                <TableCell>{job_id}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" scope="row">Peptide</TableCell>
                                <TableCell>{jobData.params.peptide}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" scope="row">MHC</TableCell>
                                <TableCell>{jobData.params.mhc}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" scope="row">Submitted</TableCell>
                                <TableCell>{formatDate(jobData.timestamps.submitted_at)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" scope="row">Started</TableCell>
                                <TableCell>{formatDate(jobData.timestamps.started_time)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" scope="row">Completed</TableCell>
                                <TableCell>{formatDate(jobData.timestamps.completed_time)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell component="th" scope="row">Failed</TableCell>
                                <TableCell>{formatDate(jobData.timestamps.failed_time)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {isRunning && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Status will update automatically...</Typography>
                    </Box>
                )}

                {isFailure && (
                    <Alert severity="error">
                        This job did not terminate successfully.
                        {jobData.error && <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>Error: {jobData.error}</pre>}
                    </Alert>
                )}
                
                {isSuccess && (
                    <Box>
                        <Alert severity="success">
                            Your job has completed successfully!
                        </Alert>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ mt: 2, textTransform: 'none' }}
                            onClick={() => navigate(resultsPageUrl)}
                        >
                            Go to Results Page
                        </Button>
                    </Box>
                )}
            </TextBackground>
        </section>
    );
};